// AnalysisResults.tsx - Updated to work with new processing logic

import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from "@/components/ui/dialog";
import StreamlitStyleChecklist from "./StreamlitStyleChecklist";
import SimpleDocumentViewer from "@/components/common/SimpleDocumentViewer";

// Import new types
import { CheckResult } from "@/lib/checkDefinitions";
import { AnalysisParameters, DocumentFiles } from "@/lib/workingDocumentProcessor";

interface AnalysisResultsProps {
  results: Record<string, CheckResult>;
  parameters: AnalysisParameters;
  documents?: DocumentFiles;
  onResultsUpdate?: (updatedResults: Record<string, CheckResult>) => void;
  onCompanyNameConfirm?: (companyName: string) => void;
}

const AnalysisResults: React.FC<AnalysisResultsProps> = ({ 
  results, 
  parameters, 
  documents = {},
  onResultsUpdate,
  onCompanyNameConfirm
}) => {
  // State for document viewer
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedDocumentUrl, setSelectedDocumentUrl] = useState<string>("");
  const [selectedDocumentName, setSelectedDocumentName] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  
  // State for company name dialog
  const [companyNameDialogOpen, setCompanyNameDialogOpen] = useState(false);
  const [companyName, setCompanyName] = useState(parameters.company_name || "");
  const [companyNameError, setCompanyNameError] = useState("");

  // State for updated results with comments
  const [updatedResults, setUpdatedResults] = useState<Record<string, CheckResult>>(results);

  // Show company name dialog on component mount only if company name is not set
  useEffect(() => {
    // Only show dialog if company name is empty or not meaningful
    if (!parameters.company_name || parameters.company_name.trim() === '') {
      const timer = setTimeout(() => {
        setCompanyNameDialogOpen(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [parameters.company_name]);

  // Check for common errors
  React.useEffect(() => {
    try {
      // Check if parameters and audit_date exist and are valid
      if (!parameters) {
        setError("Analysis parameters are missing");
        return;
      }

      // Ensure audit_date is properly handled
      if (parameters.audit_date) {
        // If it's already a Date object
        if (parameters.audit_date instanceof Date) {
          parameters.audit_date.getTime(); // This will throw if invalid
        }
        // If it's a number (timestamp)
        else if (typeof parameters.audit_date === 'number') {
          new Date(parameters.audit_date).getTime();
        }
        // If it's a string (ISO date)
        else if (typeof parameters.audit_date === 'string') {
          new Date(parameters.audit_date).getTime();
        }
        // If it's something else, convert to string first
        else {
          new Date(String(parameters.audit_date)).getTime();
        }
      }
      setError(null);
    } catch (err) {
      console.error("Date parsing error:", err);
      setError("Cannot read properties of undefined (reading 'getTime')");
    }
  }, [parameters]);

  // Handle document viewing
  const handleViewDocument = (file: File, documentType: string) => {
    try {
      console.log(`Opening document viewer for ${documentType}`);
      
      // Create object URL for the file
      const documentUrl = URL.createObjectURL(file);
      
      // Set document info and open viewer
      setSelectedDocumentUrl(documentUrl);
      setSelectedDocumentName(documentType);
      setViewerOpen(true);
      
    } catch (err) {
      console.error("Error in handleViewDocument:", err);
      setError(`Failed to open document viewer: ${err instanceof Error ? err.message : "Unknown error"}`);
    }
  };

  // Close document viewer and cleanup
  const handleCloseViewer = () => {
    if (selectedDocumentUrl) {
      URL.revokeObjectURL(selectedDocumentUrl); // Cleanup object URL
    }
    setViewerOpen(false);
    setSelectedDocumentUrl("");
    setSelectedDocumentName("");
  };

  // Handle comment changes
  const handleCommentChange = (checkId: string, comment: string) => {
    const newResults = {
      ...updatedResults,
      [checkId]: {
        ...updatedResults[checkId],
        auditorComment: comment
      }
    };
    setUpdatedResults(newResults);
    onResultsUpdate?.(newResults);
  };

  // Handle company name confirmation
  const handleCompanyNameConfirm = () => {
    if (!companyName.trim()) {
      setCompanyNameError("Company name is required");
      return;
    }
    setCompanyNameError("");
    setCompanyNameDialogOpen(false);
    onCompanyNameConfirm?.(companyName.trim());
  };

  // Document type to label mapping
  const documentLabels: Record<keyof DocumentFiles, string> = {
    audit_report: "Audit Report",
    annexure_a: "Annexure A (CARO)",
    annexure_b: "Annexure B (IFC)", 
    balance_sheet: "Balance Sheet",
    notes: "Notes to Accounts",
    pl_notes: "Profit & Loss Notes",
    annual_report: "Annual Report",
    secretarial_compliance: "Secretarial Compliance",
    cfs: "Consolidated Financial Statements (CFS)"
  };

  return (
    <div className="space-y-6">
      {/* Company Name Dialog */}
      <Dialog open={companyNameDialogOpen} onOpenChange={setCompanyNameDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Enter Company Name</DialogTitle>
            <DialogDescription>
              Please enter or confirm the company name for this analysis. This will be used to identify the report in your history.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="company-name">Company Name <span className="text-red-500">*</span></Label>
              <Input
                id="company-name"
                placeholder="e.g., ABC Corporation Ltd."
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  setCompanyNameError("");
                }}
                className={companyNameError ? "border-red-500" : ""}
              />
              {companyNameError && (
                <p className="text-sm text-red-500">{companyNameError}</p>
              )}
            </div>
            <div className="bg-blue-50 p-3 rounded-md">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> You can add comments to each checklist item below. The company name and comments will be saved together with your analysis.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleCompanyNameConfirm} className="w-full">
              Confirm and Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-500">Company Name</div>
            <div className="font-semibold text-lg">{companyName || parameters.company_name}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-500">Audit Date</div>
            <div className="font-semibold text-lg">
              {(() => {
                try {
                  if (!parameters.audit_date) return "Not specified";

                  // Handle different types of audit_date
                  if (parameters.audit_date instanceof Date) {
                    return format(parameters.audit_date, "PPP");
                  } else if (typeof parameters.audit_date === 'number') {
                    return format(new Date(parameters.audit_date), "PPP");
                  } else if (typeof parameters.audit_date === 'string') {
                    return format(new Date(parameters.audit_date), "PPP");
                  } else {
                    return format(new Date(String(parameters.audit_date)), "PPP");
                  }
                } catch (err) {
                  console.error("Error formatting date:", err);
                  return "Invalid date";
                }
              })()}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-500">Report Type</div>
            <div className="font-semibold text-lg">
              {parameters.audit_report_type}
              {parameters.profit_or_loss && ` (${parameters.profit_or_loss})`}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Document Viewer Section */}
      {documents && Object.keys(documents).some(key => documents[key as keyof DocumentFiles]) && (
        <Card className="mt-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Analyzed Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(documents).map(([docType, file]) => {
                if (!file) return null;
                
                const label = documentLabels[docType as keyof DocumentFiles] || docType;
                
                return (
                  <Button
                    key={docType}
                    variant="outline"
                    className="flex items-center gap-2"
                    onClick={() => handleViewDocument(file, label)}
                  >
                    <FileText className="h-4 w-4" />
                    View {label}
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document Viewer Dialog */}
      <SimpleDocumentViewer
        url={selectedDocumentUrl}
        title={selectedDocumentName}
        isOpen={viewerOpen}
        onClose={handleCloseViewer}
      />

      {/* Error Display */}
      {error ? (
        <div className="space-y-6 mt-6 p-6 border rounded-lg bg-white shadow-sm">
          <div className="bg-red-500 text-white p-4 rounded-md">
            <h2 className="text-xl font-bold mb-2">Analysis Failed</h2>
            <p>An error occurred during analysis: {error}</p>
          </div>
        </div>
      ) : (
        /* Main Results Display */
        <StreamlitStyleChecklist
          results={updatedResults}
          parameters={parameters}
          documents={documents}
          onCommentChange={handleCommentChange}
        />
      )}
    </div>
  );
};

export default AnalysisResults;